import { VisuallyHidden } from '@coop/components';
import { useRef } from 'react';

import { Helpers } from '../../../../utility/helpers';
import A11yAnnouncer from '../../../common/components/A11yAnnouncer/A11yAnnouncer';
import { useA11yLogInConsoleEffect } from '../../../common/hooks/useA11yLogInConsole';
import { useAppSelector } from '../../../common/hooks/useThunkDispatch';
import { PriceType } from '../../../common/models/priceData/priceData';
import { cartSelectors } from '../../../common/selectors/cartSelectors';
import cartItemsSelectors from '../../selectors/cartItemsSelectors';

const getCartSummaryAccessibilityText = (
    totalCartQuantity: number,
    totalPrice: number | undefined,
    priceType?: PriceType,
) => {
    let cartCountLabel;

    if (totalCartQuantity === 0) {
        cartCountLabel = 'Din varukorg är tom';
    } else if (totalCartQuantity === 1) {
        cartCountLabel = 'Totalt har du en vara i varukorgen';
    } else if (totalCartQuantity > 1) {
        cartCountLabel = `Totalt har du ${totalCartQuantity} varor i varukorgen`;
    }

    let totalPriceLabel;
    if (totalPrice) {
        totalPriceLabel = `Totalpris är ${Helpers.toEcommercePriceWithCurrencyAriaLabel(totalPrice)}`;
        if (priceType === PriceType.ExcludeTax) {
            totalPriceLabel += ' exklusive moms';
        }
    }

    return `${[cartCountLabel, totalPriceLabel].filter(Boolean).join('. ')}`;
};

interface CartSummaryAccessibilityLoggerProps {
    totalPrice?: number; // when we will be able to display cart value in CnCTrigger, this should be read from Redux instead
    currentPriceType?: PriceType;
    delay?: boolean;
    announceOnChange?: boolean; // if true, will announce changes in cart summary only, not on initial render
}

const CartSummaryAccessibilityLogger = (props: CartSummaryAccessibilityLoggerProps) => {
    const totalCartQuantity = useAppSelector(cartItemsSelectors.selectCartQuantity);
    const cartIsInitialized = useAppSelector(cartSelectors.isCartInitialized);
    const cartIsLoading = useAppSelector((state) => state.cart.cartLoading.isLoading);
    // Update the text when cart is done loading, until then state will preseve the previous text
    const text =
        (cartIsInitialized &&
            !cartIsLoading &&
            getCartSummaryAccessibilityText(
                totalCartQuantity,
                props.totalPrice,
                props.currentPriceType,
            )) ||
        undefined;

    const initialText = useRef(text);
    const hasChangedText = useRef(false);
    if (!hasChangedText.current && text !== initialText.current) {
        hasChangedText.current = true;
    }

    useA11yLogInConsoleEffect('CartSummaryAccessibilityLogger', text);

    if (props.delay) {
        return (
            <VisuallyHidden aria-live="polite" aria-atomic="true">
                {text}
            </VisuallyHidden>
        );
    }

    return (
        <A11yAnnouncer
            text={props.announceOnChange && !hasChangedText.current ? undefined : text}
            announcerName="CartSummaryAccessibilityLogger"
        />
    );
};

export default CartSummaryAccessibilityLogger;
